import { supabase } from '../../supabase';
import type { Snap, SnapCategory } from '@/types/database.types';
import { getLikeStatus } from '../likes';
import { generateCardImage } from '@/utils/card-generator';
import { uploadSnapCardImage, deleteSnapCardImage, uploadSnapImage } from '@/lib/supabase-storage';
import { isGoogleDriveUrl, extractGoogleDriveFileId, convertGoogleDriveUrl } from '@/utils/image-utils';
import { getTempImage, isTempImageUrl, clearTempImages } from '@/components/common/TempImageStorage';
import { v4 as uuidv4 } from 'uuid';
import { processExternalImage } from './processExternalImage';

export async function createSnap(
  title: string,
  description: string,
  isAnonymous: boolean,
  durationDays: number,
  categories: SnapCategory[],
  options: { title: string; description: string; imageUrl: string }[],
  isPrivate: boolean = false
): Promise<Snap> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  if (categories.length === 0) {
    throw new Error('At least one category is required');
  }

  if (categories.length > 3) {
    throw new Error('Maximum of 3 categories allowed');
  }

  // Validate categories against allowed values
  const validCategories: SnapCategory[] = [
    'politics',
    'economy',
    'finance',
    'relationships',
    'lifestyle',
    'technology',
    'health',
    'society_ethics',
    'entertainment',
    'personal_growth'
  ];

  const invalidCategories = categories.filter(cat => !validCategories.includes(cat));
  if (invalidCategories.length > 0) {
    throw new Error(`Invalid categories: ${invalidCategories.join(', ')}`);
  }

  // Get user profile for creator name
  const { data: profile } = await supabase
    .from('profiles')
    .select('username')
    .eq('user_id', user.id)
    .single();

  if (!profile) {
    throw new Error('User profile not found');
  }

  // Process options and handle external images
  const processedOptions = await Promise.all(options.map(async (option, index) => {
    let finalImageUrl = option.imageUrl;

    // Check if the image URL is a temporary blob URL or an external URL
    if (option.imageUrl) {
      if (isTempImageUrl(option.imageUrl)) {
        // Handle temporary blob URLs (existing logic)
        const tempId = `option-${index}`;
        const blob = getTempImage(tempId);
        
        if (blob) {
          // Convert blob to file
          const file = new File([blob], `option-${index}-${Date.now()}.jpg`, { type: 'image/jpeg' });
          
          // Upload to Supabase
          const { url, error } = await uploadSnapImage(file);
          
          if (error || !url) {
            throw new Error(`Failed to upload image for option ${index}`);
          }
          
          finalImageUrl = url;
        } else {
          finalImageUrl = '';
        }
      } 
      // Handle Google Drive URLs
      else if (isGoogleDriveUrl(option.imageUrl)) {
        try {
          finalImageUrl = await processExternalImage(option.imageUrl, user.id);
        } catch (error) {
          console.error('Error processing Google Drive image:', error);
          finalImageUrl = '';
        }
      }
    }
    
    return {
      title: option.title,
      description: option.description,
      imageUrl: finalImageUrl
    };
  }));

  // Start a transaction
  const { data: snap, error: snapError } = await supabase
    .from('snaps')
    .insert({
      title,
      description,
      creator_id: user.id,
      is_anonymous: isAnonymous,
      is_private: isPrivate,
      duration_days: durationDays,
      ends_at: new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000).toISOString()
    })
    .select(`
      *,
      creator:user_profiles!creator_id(
        id,
        username,
        avatar_url,
        created_at
      )
    `)
    .single();

  if (snapError) throw snapError;

  // Insert categories
  for (const category of categories) {
    const { error: categoryError } = await supabase
      .from('snap_categories')
      .insert({
        snap_id: snap.id,
        category
      });

    if (categoryError) {
      // If there's an error, delete the snap and throw
      await supabase.from('snaps').delete().eq('id', snap.id);
      throw categoryError;
    }
  }

  // Insert options
  for (const option of processedOptions) {
    const { error: optionError } = await supabase
      .from('snap_options')
      .insert({
        snap_id: snap.id,
        title: option.title,
        description: option.description || '',
        image_url: option.imageUrl || ''
      });

    if (optionError) {
      // If there's an error, delete the snap and throw
      await supabase.from('snaps').delete().eq('id', snap.id);
      throw optionError;
    }
  }

  const timeRemainingString = `in ${durationDays} ${durationDays === 1 ? 'day' : 'days'}`;
  
  // Generate card image
  try {
    // Generate a card image using the client-side utility
    const cardImageBlob = await generateCardImage({
      id: snap.id,
      title,
      description,
      categories,
      options: processedOptions,
      creatorName: profile.username,
      likeCount: 0,
      viewCount: 0,
      commentCount: 0,
      timeRemaining: timeRemainingString,
      isNew: true
    });

    // Convert blob to file
    const file = new File([cardImageBlob], `${snap.id}.jpg`, { 
      type: 'image/jpeg' 
    });

    // Upload to Supabase Storage
    const { url, error } = await uploadSnapCardImage(snap.id, file);

    if (url) {
      // Update the SNAP with the card image URL
      await supabase
        .from('snaps')
        .update({ card_image_url: url })
        .eq('id', snap.id);
    } else {
      // Fallback to a placeholder image if upload fails
      const placeholderUrl = `https://via.placeholder.com/1200x630?text=${encodeURIComponent(title)}`;
      await supabase
        .from('snaps')
        .update({ card_image_url: placeholderUrl })
        .eq('id', snap.id);
    }
  } catch (error) {
    console.error('Error generating card image:', error);
    // If card image generation fails, use a placeholder
    const placeholderUrl = `https://via.placeholder.com/1200x630?text=${encodeURIComponent(title)}`;
    await supabase
      .from('snaps')
      .update({ card_image_url: placeholderUrl })
      .eq('id', snap.id);
  }

  // Clean up temporary images
  clearTempImages();

  return {
    ...snap,
    categories,
    options: [],
    isOwner: true,
    commentCount: 0,
    userLiked: false,
    isMarked: false
  };
}